import React, { useRef, useState, useCallback, useEffect } from "react";
import YouTube, { YouTubeEvent } from "react-youtube";
import type { Options } from "youtube-player/dist/types";
import "react-lazy-load-image-component/src/effects/blur.css";
// body.tsx と同様、wrapRootElement 配下(Router外)からでもルート変化を検知するため
// useLocation() ではなく globalHistory を直接購読する。
import { globalHistory } from "@gatsbyjs/reach-router";
import { stripLangPrefix } from "../../utils/i18nRoutes";
import { useDict } from "../../i18n/site/SiteLangContext";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setNextVideo,
  setIsLoaded,
  setIsShrinked,
} from "../../redux/slices/playerSlice";
import { DiscographyWithSongs } from "../../types/discography";
import { Spinner } from "flowbite-react";
import { IoClose, IoVolumeHigh, IoVolumeMute } from "react-icons/io5";
import UtilityService from "../../services/UtilityService";
import { useReducedMotion } from "../../hooks/useReducedMotion";

// "/live/xxxx/" のような2階層目(詳細ページ)かどうかを判定する。
// 例: "/" → [] (0階層) / "/live/" → ["live"] (1階層) / "/live/xxxx/" → ["live","xxxx"] (2階層)
// 多言語ページ(/en/... 等)では言語プレフィックスを除いた階層で判定する(plan/28)
const isSecondLevelPath = (pathname: string): boolean => {
  const segments = stripLangPrefix(pathname).path.split("/").filter(Boolean);
  return segments.length >= 2;
};

// 相関図 (/cgraph) と開催地マップ (/live/heatmap) はフルスクリーンの
// グラフ/マップ描画ページ。body.tsx の isCGraphPath / isLiveHeatmapPath と
// 同じ判定を用いて、フル表示の動画が描画領域を圧迫しないよう常にミニ表示に固定する。
const isFullscreenVizPath = (rawPathname: string): boolean => {
  if (!rawPathname) return false;
  const pathname = stripLangPrefix(rawPathname).path;
  return (
    pathname === "/cgraph" ||
    pathname.startsWith("/cgraph/") ||
    pathname === "/live/heatmap" ||
    pathname.startsWith("/live/heatmap/")
  );
};

// 楽曲記事(/articles/<slug>/、plan/30)。記事は公式MVを主役に独自デザインで組むため、
// 浮かぶミニプレイヤーが二つ目の動画として紙面に重ならないようにする。
const isSongArticlePath = (rawPathname: string): boolean =>
  /^\/articles\/[^/]+\/?$/.test(stripLangPrefix(rawPathname).path);

// プロフィールカード(/card/・/card/view/、plan/48)。会場で画面を見せる・受け取ったカードを読むページなので、
// 動画がカードに重ならないよう、楽曲記事と同じく消音の間は隠す。
const isProfileCardPath = (rawPathname: string): boolean =>
  /^\/card(\/view)?\/?$/.test(stripLangPrefix(rawPathname).path);

const defaultOpts: Options = {
  height: "180",
  width: "320",
  playerVars: {
    // https://developers.google.com/youtube/player_parameters
    autoplay: 0,
    mute: 1,
    // controls: 0,
    // disablekb: 1,
    enablejsapi: 1,
    playsinline: 1,
    loop: 1,
    rel: 0,
    color: "white",
    origin: "https://reol.twilightea.com/",
    widget_referrer: "https://reol.twilightea.com/",
  } as Options["playerVars"],
};

type Props = {
  playlist: DiscographyWithSongs[];
  onReady?: (event: YouTubeEvent) => Promise<void>;
  onPlay?: (event: YouTubeEvent) => Promise<void>;
  onEnd?: (event: YouTubeEvent) => Promise<void>;
  onError?: (event: YouTubeEvent) => Promise<void>;
  /** 初回描画時のパス(Body から渡す)。静的HTML生成時と hydrate 時で同じ画面にするため */
  initialPathname?: string;
};

const MainVideo: React.FC<Props> = ({
  playlist,
  initialPathname,
  onReady,
  onPlay,
  onEnd,
  onError,
}) => {
  const dispatch = useAppDispatch();
  const dict = useDict();
  const { currentVideoId, isLoaded, isShrinked, playerRef } = useAppSelector(
    (state) => state.player
  );

  const isBrowser = typeof window !== "undefined";
  const ref = useRef<YouTube>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(
    isBrowser && window.scrollY > 200
  );
  const [onPlaying, setOnPlaying] = useState(false);
  // ファサード方式: タップされるまで iframe 自体を差し込まない。
  // YouTube iframeは読み込まれた時点でタイトルバー等のネイティブUIを表示するため、
  // 明示的なユーザー操作があるまで iframe を生成しないことで白い矩形の表示を防ぐ。
  const [activated, setActivated] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  // ユーザーが一度ミュート解除した意志を保持する。
  // playerVars.mute=1 のため videoId 切り替え時にプレイヤー側がミュートに戻るので、
  // このフラグを見て onPlay 時に再度 unMute() を呼び直す。
  const hasUserUnmutedRef = useRef(false);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
  const reducedMotion = useReducedMotion();

  // 強制ミニ表示(forceMini)のページでは動画を閉じる(=フル表示に戻す)先が
  // 無いため、「非表示にする」明示的な離脱手段としてセッション中だけ記憶する。
  const PIP_DISMISSED_KEY = "reol-mainvideo-pip-dismissed";
  // sessionStorage は静的HTML生成時に読めないため、初期値は false にしてマウント後に反映する
  // (初回描画から読むと、非表示にしていたセッションで hydrate 時の画面が食い違う)
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(PIP_DISMISSED_KEY) === "1") setDismissed(true);
    } catch (_) {
      // ignore (Safari Private mode 等)
    }
  }, []);
  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(PIP_DISMISSED_KEY, "1");
    } catch (_) {
      // ignore (Safari Private mode 等)
    }
  }, []);

  // "/live/xxxx/" のような2階層目(詳細ページ)では、常に縮小表示(ミニプレイヤー)に固定する。
  const [pathname, setPathname] = useState(
    () => initialPathname ?? (isBrowser ? window.location.pathname : "")
  );
  useEffect(() => {
    const unlisten = globalHistory.listen(({ location }: { location: { pathname: string } }) => {
      setPathname(location.pathname);
    });
    return () => unlisten();
  }, []);
  // /card/ は1階層目だが、大きな動画が上にあるとカードの画面が下に押されるのでミニに固定する(消音の間は隠れる)
  const forceMini = isSecondLevelPath(pathname) || isFullscreenVizPath(pathname) || isProfileCardPath(pathname);
  // 実際に「縮小表示として描画するか」。スクロール連動の縮小(isShrinked && onPlaying)
  // に加え、2階層目のページでは常にミニ表示を強制する。
  const isMini = (isShrinked && onPlaying) || forceMini;
  // 楽曲記事では、消音のまま流れている間は隠す(マウントは保ち、遷移をまたいだ再生は壊さない)。
  // 利用者が音を出して聴いている場合は、止めるための操作が見えるよう表示したままにする。
  const hiddenOnArticle = (isSongArticlePath(pathname) || isProfileCardPath(pathname)) && isMuted;

  useEffect(() => {
    if (!isBrowser) return;
    // 「視覚効果を減らす」設定時はスクロール連動の縮小を無効化
    if (reducedMotion) {
      if (isShrinked) dispatch(setIsShrinked(false));
      return;
    }
    // 一度縮小したら、ページ遷移やスクロールでは元に戻さない(ユーザーがタップして
    // 明示的に戻すまで固定)。isShrinked が true の間はリスナー自体を張らない。
    // 2階層目のページは常にミニ表示を強制するため、この自動縮小ロジック自体が不要
    // (かつ isShrinked を実際に true にしてしまうと、他ページへ遷移した際に
    // Redux state が残って意図せずミニ表示になってしまう)。
    if (isShrinked || forceMini) return;

    // 展開した時点(このeffectが張られた時点)のスクロール位置を基準に、そこから
    // さらに一定量下にスクロールしたら縮小する。動画の本来のドキュメント位置
    // (rect.bottom)を基準にすると、タップで手動展開した直後に「その時点で既に
    // ページを深くスクロール済み」だった場合、動画は画面よりずっと上(document上)
    // にあるため rect.bottom が大きく負になり、閾値が現在のスクロール位置より
    // 小さく計算されて即座に再縮小してしまう(タップしても展開したように
    // 見えない不具合の原因)。現在のスクロール位置を基準にすることでこれを防ぐ。
    let ticking = false;
    const baseline = window.scrollY;
    const threshold = baseline + 200;

    const update = () => {
      if (window.scrollY > threshold) {
        dispatch(setIsShrinked(true));
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isBrowser, dispatch, reducedMotion, isShrinked, forceMini]);

  // 縮小表示(ミニプレイヤー)をタップすると横幅フル表示に戻す
  const handleExpand = useCallback(() => {
    dispatch(setIsShrinked(false));
  }, [dispatch]);

  // 縮小表示中、ヘッダー(h-16=64px)とまだ重なる可能性がある間はヘッダーの下に、
  // ヘッダーがスクロールで画面外に出たら本来のビューポート右上端に寄せる。
  const HEADER_HEIGHT_PX = 64;
  const [nearTop, setNearTop] = useState(
    isBrowser ? window.scrollY < HEADER_HEIGHT_PX : true
  );

  useEffect(() => {
    if (!isBrowser || !isMini) return;

    let ticking = false;
    const update = () => {
      setNearTop(window.scrollY < HEADER_HEIGHT_PX);
      ticking = false;
    };
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    update();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isBrowser, isMini]);

  const handleActivate = useCallback(() => {
    setActivated(true);
    setOnPlaying(true);
  }, []);

  const handleToggleMute = useCallback(() => {
    try {
      const player = ref.current?.internalPlayer;
      if (player) {
        if (isMuted) {
          player.unMute();
          setIsMuted(false);
          hasUserUnmutedRef.current = true;
        } else {
          player.mute();
          setIsMuted(true);
          // ユーザーが明示的にミュートし直した場合は意志をリセット
          hasUserUnmutedRef.current = false;
        }
      }
    } catch (_) { /* ignore */ }
  }, [isMuted]);

  const handleReady = useCallback(
    async (event: YouTubeEvent) => {
      try {
        if (onReady) await onReady(event);
        dispatch(setIsLoaded(ref));
      } catch (error) {
        alert("onReady: " + error?.toString());
      }
    },
    [onReady, dispatch]
  );

  const handlePlay = useCallback(
    async (event: YouTubeEvent) => {
      try {
        if (onPlay) await onPlay(event);
        // 一度ユーザーがミュート解除していた場合、次以降の動画でも自動で解除
        if (hasUserUnmutedRef.current) {
          try {
            await event.target.unMute();
            setIsMuted(false);
          } catch (_) { /* ignore */ }
        }
        UtilityService.gtag({
          category: "youtube",
          action: "play",
          label: currentVideoId,
        });
        setOnPlaying(true);
      } catch (error) {
        alert("onPlay: " + error?.toString());
      }
    },
    [onPlay, currentVideoId]
  );

  const handleEnd = useCallback(
    async (event: YouTubeEvent) => {
      try {
        if (onEnd) await onEnd(event);
        UtilityService.gtag({
          category: "youtube",
          action: "end",
          label: currentVideoId,
        });
        if (!currentVideoId) {
          setCurrentPlaylistIndex((prev) => prev + 1);
        }
      } catch (error) {
        alert("onEnd" + error?.toString());
      }
    },
    [onEnd, currentVideoId]
  );

  const handleError = useCallback(
    (event: YouTubeEvent) => {
      if (onError) onError(event);
      UtilityService.gtag({
        category: "youtube",
        action: "end",
        label: currentVideoId,
      });
      setTimeout(() => handleEnd(event), 3000);
    },
    [onError, currentVideoId, handleEnd]
  );

  // ユーザーがPiP(ミニ表示)を明示的に閉じていたら、フル表示に戻れない
  // 強制ミニ表示ページでは再表示のしようがないため、このセッション中は描画しない。
  if (dismissed && isMini) return null;

  try {
    const playlistVideos = playlist
      .filter((item) => item.xfdUrl)
      .map((item) => (item.xfdUrl ?? "").replace("https://youtu.be/", ""));

    return (
      <div
        id="PlayerContainer"
        // fixed⇔static(position自体の切り替え)はCSSでは滑らかに補間できず、
        // transitionを掛けると「幅/位置だけ新状態に近づき、position:fixedのままの
        // 期間」が発生してヘッダー裏に隠れる不具合になるため、この切り替えは
        // アニメーションさせず即時反映する。
        className={`z-20 top-0 overflow-hidden origin-top-right ${
          isMini
            ? // position:fixedはビューポート基準のため、ヘッダー(h-16=4rem, z-40)が
              // まだ画面上部にある間(nearTop)はその下に、スクロールでヘッダーが
              // 画面外に出たら本来の右上端(top-1)に寄せる。
              // 相関図/開催地マップはスクロールしない固定ビューポートページなので
              // nearTopが常にtrueのまま維持され、素朴に4.25remだと自前のツールバー
              // (検索/絞り込み/Six Degreesボタン)に重なってしまうため、
              // その高さ分さらに下にオフセットする。
              `fixed w-[min(30vw,13rem)] ${
                isFullscreenVizPath(pathname)
                  ? "top-[7.5rem]"
                  : nearTop
                  ? "top-[4.25rem]"
                  : "top-1"
              } right-1 -translate-x-1 rounded-lg`
            : "w-full top-0 right-0 left-full translate-x-0 rounded-none"
        }${hiddenOnArticle ? " invisible pointer-events-none" : ""}`}
        aria-hidden={hiddenOnArticle || undefined}
      >
        {/* 「フル表示に戻す」導線(✕・タップ展開)は、2階層目ページの強制ミニ表示
            (forceMini)では出さない。スクロール連動で縮小した場合のみ展開できる。 */}
        {isShrinked && onPlaying && (
          <button
            onClick={handleExpand}
            className="absolute top-1 right-1 z-30 bg-black/50 hover:bg-black/70 rounded-full p-1 transition-all"
            aria-label={dict.video.expand}
          >
            <IoClose className="text-white text-xl" />
          </button>
        )}
        {/* forceMini(2階層目/相関図/開催地マップ)には「フル表示に戻す」先が無いため、
            代わりにこのセッション中だけ非表示にできる閉じるボタンを出す。 */}
        {forceMini && (
          <button
            onClick={handleDismiss}
            className="absolute top-1 right-1 z-30 bg-black/50 hover:bg-black/70 rounded-full p-1 transition-all"
            aria-label={dict.video.close}
          >
            <IoClose className="text-white text-xl" />
          </button>
        )}
        {isLoaded && onPlaying && (
          <button
            onClick={handleToggleMute}
            className={`absolute z-30 bg-black/60 hover:bg-black/80 rounded-full p-1.5 transition-all ${
              isMini ? "bottom-1 left-1" : "bottom-2 left-2"
            }`}
            aria-label={isMuted ? dict.video.unmute : dict.video.mute}
          >
            {isMuted ? (
              <IoVolumeMute className="text-white text-lg" />
            ) : (
              <IoVolumeHigh className="text-white text-lg" />
            )}
          </button>
        )}
        {isShrinked && onPlaying && (
          <button
            type="button"
            onClick={handleExpand}
            aria-label={dict.video.expand}
            className="absolute inset-0 z-[25] w-full h-full cursor-pointer"
          />
        )}
        <div ref={playerWrapperRef} className={`relative w-full sm:max-h-80`}>
          {!activated ? (
            <button
              type="button"
              onClick={handleActivate}
              aria-label={dict.video.play}
              // aspect-w/aspect-h(paddingハック)はsm:max-h-80との併用時に
              // 実際の高さより大きい箱を作ってしまい、中央寄せの再生ボタンが
              // クリップされた可視範囲の外に落ちる。実CSSのaspect-ratioを使うことで
              // max-heightと正しく連動させる。
              className="group relative block w-full sm:max-h-80 overflow-hidden bg-black"
              style={{ aspectRatio: "16 / 9" }}
            >
              <img
                className="w-full h-full object-cover object-center"
                src={`https://i.ytimg.com/vi/${playlistVideos[currentPlaylistIndex]}/mqdefault.jpg`}
                alt="Video thumbnail"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-bx-yellow/90 group-hover:bg-bx-yellow transition-colors shadow-lg">
                  <span
                    aria-hidden
                    className="ml-1 border-y-[10px] border-y-transparent border-l-[16px] border-l-bx-bg"
                  />
                </span>
              </span>
            </button>
          ) : (
            <>
              <div
                className={
                  isLoaded
                    ? "hidden"
                    : "relative bg-black w-full sm:max-h-80 overflow-hidden"
                }
                style={isLoaded ? undefined : { aspectRatio: "16 / 9" }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <Spinner aria-label="main video" color="failure" size="xl" />
                </div>
                <img
                  className="w-full h-full object-cover object-center"
                  src={`https://i.ytimg.com/vi/${
                    currentVideoId
                      ? currentVideoId
                      : playlistVideos[currentPlaylistIndex]
                  }/mqdefault.jpg`}
                  alt="Video thumbnail"
                />
              </div>
              <YouTube
                videoId={
                  currentVideoId
                    ? currentVideoId
                    : playlistVideos[currentPlaylistIndex]
                }
                ref={ref}
                opts={{
                  ...defaultOpts,
                  playerVars: {
                    ...defaultOpts.playerVars,
                    // playlist: playlistVideos.join(","),
                    autoplay: 1,
                  },
                }}
                className={!isLoaded ? "invisible" : "aspect-w-16 aspect-h-9"}
                iframeClassName={`sm:max-h-80 ${isShrinked ? "" : ""}`}
                onReady={handleReady}
                onPlay={handlePlay}
                onError={handleError}
                onEnd={handleEnd}
              />
            </>
          )}
        </div>
      </div>
    );
  } catch (error) {
    alert("render: " + error?.toString());
    return null;
  }
};

export default MainVideo;
